import {
  companionCtaOpensATab,
  resolveCompanionCtaHandler,
} from '@/components/companion/cta/companion-cta-registry';
import { COMPANION_PATH, invalidateCompanionQueries } from '@/hooks/queries/companion-keys';
import api from '@/libs/axiosInstance';
import type { ApiResponse, CompanionActionDto } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseAsString, useQueryState } from 'nuqs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/**
 * What happens when a brand takes a card up on its offer.
 *
 * Two things, in this order: the card is marked opened, and the panel hands the
 * brand over to the workflow. Opening is reading, never doing — the card stays
 * on the panel until something in the business actually happens. A workflow
 * that opens a new tab is the one exception to the order: the tab opens first,
 * inside the click, and the opening is recorded right after.
 *
 * Where each workflow leads is the registry's business, not this hook's: the
 * hook records the opening and looks the destination up. A workflow with no
 * entry there is a card this build cannot honour, and nothing happens.
 *
 * Not every workflow is a route. The email one opens the composer the brand
 * already uses, with the card's draft in it and nothing sent: the message
 * leaves when the brand presses send, and only then does the card close.
 */

export {
  DISCOVERY_RECOMMENDATIONS_PATH,
  PAYOUT_PREPARE_PATH,
} from '@/components/companion/cta/companion-cta-registry';

export type CompanionCtaNavigationOptions = {
  /** Called once the card is opened and the destination is decided. */
  onNavigated?: () => void;
};

async function openCompanionAction(input: {
  actionId: string;
  version: number;
}): Promise<ApiResponse> {
  const { data } = await api.post<ApiResponse>(`${COMPANION_PATH}/actions/${input.actionId}/open`, {
    version: input.version,
  });
  return data;
}

export const useCompanionCtaNavigation = (options: CompanionCtaNavigationOptions = {}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // The same parameter the mention grids write, so the preview the card opens
  // is the one the rest of the dashboard opens.
  const [, setPreviewMention] = useQueryState('previewMention', parseAsString);

  const mutation = useMutation({
    mutationFn: openCompanionAction,
    onSettled: () => {
      invalidateCompanionQueries(queryClient);
    },
  });

  const openCta = useCallback(
    async (action: CompanionActionDto) => {
      const handler = resolveCompanionCtaHandler(action);
      const context = {
        navigate,
        queryClient,
        t,
        setPreviewMention,
        onNavigated: options.onNavigated,
      };
      const recordOpening = async () => {
        try {
          await mutation.mutateAsync({ actionId: action.id, version: action.version });
        } catch {
          // The panel is a shortcut, not a gate. A failed opening is worth a
          // refetch, never a refused navigation.
          invalidateCompanionQueries(queryClient);
        }
      };

      // A new tab has to open inside the click itself: Safari refuses one that
      // waits for anything first, so the opening is recorded after it.
      if (handler && companionCtaOpensATab(action.cta.workflow)) {
        void handler(context, action);
        await recordOpening();
        return;
      }

      // Everywhere else the opening is recorded first: a brand that lands in
      // Discovery on a card still marked unread would be shown it again tomorrow.
      await recordOpening();
      if (!handler) return;

      await handler(context, action);
    },
    [mutation, navigate, options, queryClient, setPreviewMention, t]
  );

  return { openCta, isPending: mutation.isPending };
};
